import React from 'react'
import styled from 'styled-components'
import {
  formatMetricValue,
  formatMinMaxRange,
  getSourceColor,
} from '../utils/zoningHelpers'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
  AuthorizedValue,
} from './ZoneMetricsTable.styles'

const EditableInput = styled.input<{
  $hasOverride?: boolean
  $isPending?: boolean
}>`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ $hasOverride, $isPending, theme }) =>
    $isPending
      ? theme.colors.primary
      : $hasOverride
        ? '#00B341'
        : theme.colors.text.primary};
  font-weight: ${({ $hasOverride, $isPending }) =>
    $hasOverride || $isPending ? '600' : '400'};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.background.secondary};
    cursor: not-allowed;
  }
`

const RowWithOptionalBorder = styled.tr<{ $showBorder: boolean }>`
  td {
    height: ${({ theme }) => theme.spacing.xlarge};
    padding: ${({ theme }) =>
      `${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.xsmall} ${theme.spacing.medium}`};
    border-bottom: ${({ $showBorder, theme }) =>
      $showBorder ? `1px solid ${theme.colors.border.light}` : 'none'};
  }
`

export interface StandardMetricRowProps {
  metricKey: string
  metricLabel: string
  metricDescription?: string
  isEditing: boolean

  // Values
  currentValue?: number
  minValue?: number
  maxValue?: number

  // Sources
  minSource?: string
  maxSource?: string
  minReference?: any
  maxReference?: any

  // State
  isOverridden: boolean
  isPending: boolean
  unit?: string

  // Display
  showBorder?: boolean // Control border visibility

  // Handlers
  onValueChange: (metricKey: string, value: number) => void
  onAuthorizedClick: (metricKey: string) => void
}

/**
 * Standard row component for regular metrics (heights, floors, other)
 * No radio selector, just displays values with edit capability
 */
export const StandardMetricRow: React.FC<StandardMetricRowProps> = ({
  metricKey,
  metricLabel,
  // metricDescription,
  isEditing,
  currentValue,
  minValue,
  maxValue,
  // minSource,
  maxSource,
  // minReference,
  // maxReference,
  isOverridden,
  isPending,
  unit,
  showBorder = false,
  onValueChange,
  onAuthorizedClick,
}) => {
  // Calculate difference
  const calculateDifference = (): {
    value: number
    isPositive: boolean
  } | null => {
    if (maxValue === undefined || currentValue === undefined) return null
    const diff = maxValue - currentValue
    return { value: diff, isPositive: diff >= 0 }
  }

  const diff = calculateDifference()

  // Format authorized value
  const formatAuthorized = (): string => {
    if (minValue !== undefined && maxValue !== undefined) {
      return formatMinMaxRange(minValue, maxValue, unit)
    }
    if (maxValue !== undefined) {
      return formatMetricValue(maxValue, unit, metricKey)
    }
    if (minValue !== undefined) {
      return formatMetricValue(minValue, unit, metricKey)
    }
    return '-'
  }

  const authorizedDisplay = formatAuthorized()
  const sourceColor = getSourceColor(maxSource, isOverridden)

  return (
    <RowWithOptionalBorder $showBorder={showBorder}>
      <td>
        <MetricLabel>{metricLabel}</MetricLabel>
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, metricKey)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {isEditing ? (
          <EditableInput
            type="number"
            step="0.01"
            value={maxValue !== undefined ? maxValue : ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value)
              if (!isNaN(value)) {
                onValueChange(`${metricKey}_max`, value)
              }
            }}
            $hasOverride={isOverridden}
            $isPending={isPending}
            placeholder="0.00"
          />
        ) : authorizedDisplay !== '-' ? (
          <AuthorizedValue
            $source={maxSource}
            $isUserEdited={isOverridden}
            $isPending={isPending}
            onClick={() => onAuthorizedClick(metricKey)}
            style={{ cursor: 'pointer', color: sourceColor }}
            title="Click to view source details"
          >
            {authorizedDisplay}
          </AuthorizedValue>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={diff?.isPositive ?? true}>
        {diff ? (
          <>
            {diff.isPositive && '+'}
            {formatMetricValue(diff.value, unit, metricKey)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </RowWithOptionalBorder>
  )
}
